import * as React from "react";
import { FC, useEffect, useMemo, useCallback, useState, ChangeEvent } from "react";
import _ from "lodash";
import fp from "lodash/fp";
import { connect, Dispatch } from "react-redux";
import { format } from "lib/globalize-util";
import makeStyles from "@material-ui/core/styles/makeStyles";
import TabContext from "@material-ui/lab/TabContext";
import { MediaDialog } from "components/dialog/media-dialog";
import { FormControlLabel } from "@material-ui/core";
import Checkbox from "@material-ui/core/Checkbox";
import { IStore } from "reducers";
import { ManageMediaTable } from "components/product-media/manage-media-table";
import { arrayMove } from "react-sortable-hoc";
import {
    fetchMediaAssets,
    MUTATE_OPERATION_TYPES,
    mutateMediaAssets,
    resetProductMediaAssets,
} from "actions/product-media-actions";
import { IMediaAsset, IMediaAssetMap, IUpdateMediaAssetOperation } from "constants/media-asset";
import {
    StyledAppBar,
    StyledTab,
    StyledTabPanel,
    StyledTabs,
} from "components/product-collection/product-collection-views/product-collection-item-modal";
import { ManageUnlinkedTable } from "./manage-unlinked-table";
import { add as snackbarAdd, SnackbarAddPayload } from "actions/snackbars";
import {
    fetchUnlinkedMedia,
    IFetchUnlinkedMediaParams,
    linkMediaToProduct,
    reset as resetUnlinkedMedia,
    markMediaAsLinked,
} from "actions/unlinked-media-actions";
import { ICoreUnlinkedMedia, IUnlinkedMedia } from "constants/unlinked-media";
import { Form } from "formsy-react";
import { themeOptions } from "styles/theme";
import SearchIcon from "material-ui/svg-icons/action/search";
import { useDebounceFunction } from "hooks/useDebounce";
import { TextField } from "components/form";
import Button from "@material-ui/core/Button";
import AddIcon from "@material-ui/icons/Add";
import type { IScope } from "reducers/scope";
import { MEDIA_TYPE_IMAGE, MEDIA_TYPE_THREEDIUM, MEDIA_TYPE_VIDEO, MAX_PRODUCT_MEDIA_COUNT } from "constants/media";
import { createEmptyMedia, getSortedMediaKeys, transformMediaMap } from "helpers/manage-media";
import { DATATEST } from "constants/integration";

interface IProps {
    open: boolean;
    productId: string;
    onClose(reload: boolean): void;
}

interface IReduxProps {
    scope: IScope;
    touchDevice: boolean;
    mediaAssetMap: IMediaAssetMap;
    coreUnlinkedMedia: ICoreUnlinkedMedia[];
    revision: string;
    isUpdatingMediaAssets: boolean;
    isFetchingMediaAssets: boolean;
    isUpdatingUnlinkedMedia: boolean;
    isFetchingUnlinkedMedia: boolean;
    unlinkedMediaHasNextPage: boolean;
}

interface IDispatchProps {
    onResetMediaAssets(): void;
    onResetUnlinkedMedia(): void;
    fetchMedia(id: string): void;
    fetchUnlinkedMedia(merchantId: string, params?: IFetchUnlinkedMediaParams): void;
    updateMedia(
        productId: string,
        revision: string,
        mediaMap: IMediaAssetMap,
        operations: IUpdateMediaAssetOperation[],
    ): void;
    onUpdateLinksState(merchant_id: string, mediaMap: IMediaAssetMap): void;
    onLinkMedia(
        productId: string,
        revision: string,
        mediaMap: IMediaAssetMap,
        operations: IUpdateMediaAssetOperation[],
    ): void;
    onSnackbarAdd(params: SnackbarAddPayload): void;
}

const TABS = { PRODUCT_MEDIA: "product_media", UNLINKED_MEDIA: "unlinked_media" };

const ManageMediaDialog: FC<IProps & IReduxProps & IDispatchProps> = (props) => {
    const { open, productId, onClose } = props;
    const {
        revision,
        isUpdatingMediaAssets,
        isFetchingMediaAssets,
        isUpdatingUnlinkedMedia,
        isFetchingUnlinkedMedia,
        unlinkedMediaHasNextPage = false,
    } = props;
    const { coreUnlinkedMedia, mediaAssetMap } = props;
    const { touchDevice, scope } = props;

    const { fetchMedia, updateMedia, fetchUnlinkedMedia } = props;
    const { onResetMediaAssets, onResetUnlinkedMedia, onLinkMedia, onUpdateLinksState, onSnackbarAdd } = props;

    const [activeTab, setActiveTab] = useState(TABS.PRODUCT_MEDIA);
    const [mediaMap, setMediaMap] = useState<IMediaAssetMap>({});
    const [unlinkedMedia, setUnlinkedMedia] = useState<IUnlinkedMedia[]>([]);
    const [updateOperations, setUpdateOperations] = useState<IUpdateMediaAssetOperation[]>([]);
    const [showAllImages, setShowAllImages] = useState<boolean>(false);

    const [close, setClose] = useState<boolean>(false); // close the dialog right after update is finished
    const [saving, setSaving] = useState<boolean>(false);

    const [searchText, setSearchText] = useState<string>("");

    const [selectedUnlinkedMedia, setSelectedUnlinkedMedia] = useState<IUnlinkedMedia[]>([]);

    const isSaveDisabled = useMemo(() => {
        return isUpdatingMediaAssets || isFetchingMediaAssets || isFetchingUnlinkedMedia || isUpdatingUnlinkedMedia;
    }, [isUpdatingMediaAssets, isFetchingMediaAssets, isUpdatingUnlinkedMedia, isFetchingUnlinkedMedia]);

    const media = useMemo<IMediaAsset[]>(() => transformMedia(mediaMap), [mediaMap]);

    const merchantId = useMemo(() => scope?.brand?.merchant_id || "", [scope]);

    useEffect(() => {
        fetchMedia(productId);
        fetchUnlinkedMedia(merchantId);
    }, [fetchMedia, fetchUnlinkedMedia, productId, merchantId]);

    useEffect(() => {
        setMediaMap(transformMediaMap(mediaAssetMap));
    }, [mediaAssetMap]);

    useEffect(() => {
        setUnlinkedMedia(transformUnlinkedMedia(coreUnlinkedMedia));
    }, [coreUnlinkedMedia]);

    const handleClose = useCallback(
        (reload?: boolean) => {
            onResetMediaAssets();
            onResetUnlinkedMedia();
            onClose(reload === true);
        },
        [onClose, onResetUnlinkedMedia, onResetMediaAssets],
    );

    useEffect(() => {
        if (close && !isSaveDisabled) {
            handleClose(true);
        }
    }, [close, isSaveDisabled, handleClose]);

    useEffect(() => {
        if (isUpdatingMediaAssets) {
            setSaving(true);
        } else if (saving && !isUpdatingMediaAssets) {
            setClose(true);
            setSaving(false);
        }
    }, [saving, isUpdatingMediaAssets]);

    const handleTabChange = useCallback((event: ChangeEvent, newValue: string) => {
        setActiveTab(newValue);
    }, []);

    const handleDeleteMedia = useCallback(
        (index: number) => {
            const orderedKeys = _.keys(mediaMap);
            updateOperations.push({ [MUTATE_OPERATION_TYPES.REMOVE]: { index: orderedKeys[index] } });
            setUpdateOperations(updateOperations);
            const updatedMap = transformMediaMap(deleteMediaByKey(mediaMap, `${index + 1}`));
            setMediaMap(normalizeMediaSequence(updatedMap, transformMedia(updatedMap)));
        },
        [mediaMap, updateOperations],
    );

    const handleUpdateMediaIndex = useCallback(
        (oldIndex: number, newIndex: number) => {
            const updatedMedia = reorderMedia(media, mediaMap, oldIndex, newIndex);
            setMediaMap(normalizeMediaSequence(mediaMap, updatedMedia));
        },
        [media, mediaMap],
    );

    const validateMediaMap = useCallback(
        (unlinkedMedia: IMediaAssetMap): boolean => {
            const assetCount = _.keys(unlinkedMedia).length;
            if (assetCount > MAX_PRODUCT_MEDIA_COUNT) {
                onSnackbarAdd({
                    message: format(
                        "There are no enough available spaces to add images/media. Select fewer and try again.",
                    ),
                });
                return false;
            }
            return true;
        },
        [onSnackbarAdd],
    );

    const handleSave = useCallback(() => {
        const updatedMediaMap = _.pickBy(mediaMap, ({ emptyMedia }) => !emptyMedia);
        const unlinkedMedia = _.pickBy(updatedMediaMap, ({ unlinkedMedia }) => unlinkedMedia);
        if (!validateMediaMap(updatedMediaMap)) {
            return;
        }
        if (!_.isEmpty(unlinkedMedia)) {
            onLinkMedia(productId, revision, updatedMediaMap, updateOperations);
            onUpdateLinksState(merchantId, updatedMediaMap);
        } else {
            updateMedia(productId, revision, updatedMediaMap, updateOperations);
        }
    }, [
        merchantId,
        onLinkMedia,
        productId,
        validateMediaMap,
        revision,
        mediaMap,
        updateMedia,
        updateOperations,
        onUpdateLinksState,
    ]);

    const searchChangeCallback = React.useCallback(
        (searchQuery: string, showAllImages: boolean) => {
            onResetUnlinkedMedia();
            fetchUnlinkedMedia(merchantId, { search: searchQuery, includeLinked: showAllImages });
        },
        [fetchUnlinkedMedia, merchantId, onResetUnlinkedMedia],
    );

    const debounceSearchChange = useDebounceFunction(searchChangeCallback, 250);

    const handleSearchChange = useCallback(
        (e, value: string) => {
            setSearchText(value);
            debounceSearchChange(value, showAllImages);
        },
        [showAllImages, debounceSearchChange],
    );

    const handleSelectedMediaUpdate = useCallback(
        (unlinkedMediaItem: IUnlinkedMedia) => () => {
            const index = _.findIndex(selectedUnlinkedMedia, (item) => item.id === unlinkedMediaItem.id);
            if (index >= 0) {
                selectedUnlinkedMedia.splice(index, 1);
            } else {
                selectedUnlinkedMedia.push(unlinkedMediaItem);
            }
            setSelectedUnlinkedMedia([...selectedUnlinkedMedia]);
        },
        [selectedUnlinkedMedia],
    );

    const handleFetchNextUnlimitedMedia = useCallback(() => {
        if (unlinkedMediaHasNextPage) {
            const lastFilename = _.last(unlinkedMedia).filename;
            fetchUnlinkedMedia(merchantId, { search: searchText, lastFilename, includeLinked: showAllImages });
        }
    }, [searchText, unlinkedMedia, unlinkedMediaHasNextPage, merchantId, fetchUnlinkedMedia, showAllImages]);

    const handleAddUnlinkedImages = useCallback(() => {
        const ids = _.map(selectedUnlinkedMedia, "id");
        const updated = _.filter(unlinkedMedia, (item) => !_.includes(ids, item.id));
        const updatedMediaMap = addUnlinkedMediaToMap(selectedUnlinkedMedia, mediaMap);
        if (!validateMediaMap(updatedMediaMap)) {
            return;
        }
        setUnlinkedMedia(updated);
        setMediaMap(updatedMediaMap);
        setSelectedUnlinkedMedia([]);
    }, [selectedUnlinkedMedia, unlinkedMedia, mediaMap, validateMediaMap]);

    const handleShowAllImagesChange = useCallback(
        (_, checked) => {
            setShowAllImages(checked);
            onResetUnlinkedMedia();
            fetchUnlinkedMedia(merchantId, { search: searchText, includeLinked: checked });
        },
        [searchText, setShowAllImages, onResetUnlinkedMedia, fetchUnlinkedMedia, merchantId],
    );

    const classes = useStyles({});
    return (
        <MediaDialog
            classes={{ paper: classes.paper }}
            open={open}
            title={format("Manage Media/Images")}
            onClose={handleClose}
            onSave={handleSave}
            disabledSave={isSaveDisabled}
            customActions={
                activeTab === TABS.UNLINKED_MEDIA && (
                    <FormControlLabel
                        control={
                            <Checkbox
                                inputProps={{ "data-test": DATATEST.MEDIA.UNLINKED.SHOW_ALL } as any}
                                checked={showAllImages}
                                onChange={handleShowAllImagesChange}
                                name="showAllImages"
                                color="primary"
                            />
                        }
                        label={format("Show all images, not just unlinked")}
                    />
                )
            }
        >
            <TabContext value={activeTab}>
                <StyledAppBar position="static" color="default">
                    <StyledTabs onChange={handleTabChange}>
                        <StyledTab
                            data-test={DATATEST.MEDIA.TABS.MANAGE_MEDIA}
                            value={TABS.PRODUCT_MEDIA}
                            label={format("Manage")}
                        />
                        <StyledTab
                            data-test={DATATEST.MEDIA.TABS.MANAGE_UNLINKED_MEDIA}
                            value={TABS.UNLINKED_MEDIA}
                            label={format("Unlinked Images")}
                        />
                    </StyledTabs>
                </StyledAppBar>
                <StyledTabPanel value={TABS.PRODUCT_MEDIA} className={classes.tabPanel}>
                    <div className={classes.contentTitle}>
                        {format(
                            "Use the controls to change the display order of images & media in the Image & Media Gallery.",
                        )}
                    </div>
                    <ManageMediaTable
                        isFetching={isFetchingMediaAssets}
                        media={media}
                        touchDevice={touchDevice}
                        onDelete={handleDeleteMedia}
                        onUpdateMediaIndex={handleUpdateMediaIndex}
                    />
                </StyledTabPanel>
                <StyledTabPanel value={TABS.UNLINKED_MEDIA} className={classes.tabPanel}>
                    <div data-test={DATATEST.MEDIA.UNLINKED.TAB_TITLE} className={classes.contentTitle}>
                        {format("Select images & media to link them to the product.")}
                    </div>
                    <Form className={classes.actionsContainer}>
                        <Button
                            data-test={DATATEST.MEDIA.UNLINKED.ADD_BUTTON}
                            className={classes.addFieldButton}
                            onClick={handleAddUnlinkedImages}
                        >
                            <AddIcon className={classes.addIcon} fontSize="large" />
                            <span>{format("Add")}</span>
                        </Button>
                        <div>
                            <SearchIcon className={classes.searchIcon} />
                            <TextField
                                dataTest={DATATEST.MEDIA.UNLINKED.SEARCH}
                                hintText={format("Search")}
                                large={false}
                                onChange={handleSearchChange}
                                value={searchText}
                                name="unlinked-media-search"
                                style={styles.textField}
                                inputStyle={styles.input}
                                hintStyle={styles.hint}
                                allowWhitespace={true}
                                canClear={true}
                            />
                        </div>
                    </Form>
                    <ManageUnlinkedTable
                        scope={scope}
                        isFetching={isFetchingUnlinkedMedia}
                        unlinkedMedia={unlinkedMedia}
                        onFetchNext={handleFetchNextUnlimitedMedia}
                        selectedMedia={selectedUnlinkedMedia}
                        onSelectedMediaUpdate={handleSelectedMediaUpdate}
                    />
                </StyledTabPanel>
            </TabContext>
        </MediaDialog>
    );
};

const deleteMediaByKey = (mediaMap: IMediaAssetMap, key: string) => {
    delete mediaMap[key];
    return mediaMap;
};

const reorderMedia = (media: IMediaAsset[], mediaMap: IMediaAssetMap, oldIndex: number, newIndex: number) => {
    const sourceKey = _.parseInt(media[oldIndex].key, 10);
    const targetKey = _.parseInt(media[newIndex].key, 10);
    const reorderedMedia = arrayMove(transformMedia(mediaMap), sourceKey - 1, targetKey - 1);
    return _.reduce(
        reorderedMedia,
        (result, item, index) => {
            if (!item.emptyMedia) {
                result.push({ ...item, key: _.toString(index + 1) });
            }
            return result;
        },
        [],
    );
};

const transformMedia = (mediaMap: IMediaAssetMap): IMediaAsset[] => {
    return fp.compose(
        fp.map((key) => ({
            ...mediaMap[key],
            source: `${mediaMap[key].url}?max_dimension=150`,
            key: key,
        })),
        fp.keys,
    )(mediaMap);
};

const normalizeMediaSequence = (mediaMap: IMediaAssetMap, media: IMediaAsset[]): IMediaAssetMap => {
    return fp.compose(
        fp.reduce((result, key) => {
            const mediaAsset = _.find(media, ["key", key]);
            result[key] = mediaAsset ? { ...mediaAsset } : createEmptyMedia();
            return result;
        }, {}),
        fp.keys,
    )(mediaMap);
};

const transformUnlinkedMedia = (coreUnlinkedMedia: ICoreUnlinkedMedia[]): IUnlinkedMedia[] => {
    return _.map(coreUnlinkedMedia, (unlinkedMediaItem) => {
        const unlinkedBase = { ...unlinkedMediaItem, type: unlinkedMediaItem.kind, url: unlinkedMediaItem.url };
        const unlinkedMedia = {
            [MEDIA_TYPE_IMAGE]: {
                highResolutionSource: `${unlinkedMediaItem.url}?max_dimension=1920`,
                lowResolutionSource: `${unlinkedMediaItem.url}?max_dimension=210`,
            },
            [MEDIA_TYPE_VIDEO]: {
                highResolutionSource: `${unlinkedMediaItem.url}?video=high`,
                lowResolutionSource: `${unlinkedMediaItem.url}?video=standard`,
                highResolutionPoster: `${unlinkedMediaItem.url}?max_dimension=1920`,
                lowResolutionPoster: `${unlinkedMediaItem.url}?max_dimension=210`,
            },
            [MEDIA_TYPE_THREEDIUM]: {
                threedium_id: unlinkedMediaItem.threedium_id,
            },
        };
        return { ...unlinkedBase, ...unlinkedMedia[unlinkedMediaItem.kind] } as IUnlinkedMedia;
    });
};

const addUnlinkedMediaToMap = (unlinkedMedia: IUnlinkedMedia[], mediaMap: IMediaAssetMap): IMediaAssetMap => {
    const lastKey = getSortedMediaKeys(mediaMap).pop() || 0;
    const result = { ...mediaMap };
    _.forEach(unlinkedMedia, (mediaItem, index) => {
        const key = lastKey + index + 1;
        result[key] = {
            ..._.pick(mediaItem, ["id", "filename", "type", "url", "image_id", "media_id", "upload_id"]),
            asset_id: "",
            source: "",
            unlinkedMedia: true,
        } as IMediaAsset;
    });
    return result;
};

const styles = {
    textField: {
        width: "80%",
    },
    input: {
        border: "none",
        width: "100%",
        height: "50px",
        fontSize: "16px",
        paddingLeft: "16px",
    },
    hint: {
        paddingLeft: "16px",
        bottom: "-10px",
    },
};

const useStyles = makeStyles({
    dialog: {
        zIndex: "1500 !important" as any,
    },
    paper: {
        maxWidth: 955,
        width: "100%",
    },
    content: {
        position: "relative",
        paddingTop: 20,
        paddingBottom: 20,
    },
    contentTitle: {
        fontSize: 14,
        padding: 10,
    },
    tabPanel: {
        padding: 0,
    },
    actionsContainer: {
        padding: "0 10px",
        display: "flex",
        justifyContent: "space-between",
        borderBottom: "1px solid #bdbdbd",
        margin: "0 0 25px",
    },
    searchIcon: {
        float: "left",
        margin: "12px 0 0 10px",
    },
    checkbox: {
        backgroundColor: "transparent !important",
        color: themeOptions.palette.grey,
        "& .MuiSvgIcon-root": {
            fontSize: 24,
        },
        "&.Mui-checked": {
            color: themeOptions.palette.primary1Color,
        },
    },
    addFieldButton: {
        width: "150px",
    },
    addIcon: {
        color: themeOptions.palette.primary1Color,
        marginRight: "20px",
    },
    confirmationDialog: {
        zIndex: "1501 !important" as any,
    },
});

const mapStateToProps = (store: IStore): IReduxProps => ({
    scope: store.scope,
    touchDevice: store.app.touchDevice,
    coreUnlinkedMedia: store.unlinkedMedia?.payload || [],
    mediaAssetMap: store.productMediaAssets?.payload?.media as IMediaAssetMap,
    revision: store.productMediaAssets?.payload?.revision,
    isFetchingMediaAssets: store.productMediaAssets?.isFetching,
    isUpdatingMediaAssets: store.productMediaAssets?.isUpdating,
    isFetchingUnlinkedMedia: store.unlinkedMedia?.isFetching,
    isUpdatingUnlinkedMedia: store.unlinkedMedia?.isUpdating,
    unlinkedMediaHasNextPage: store.unlinkedMedia?.hasNextPage,
});

const mapDispatchToProps = (dispatch: Dispatch<{}>): IDispatchProps => ({
    onResetMediaAssets: () => dispatch(resetProductMediaAssets()),
    onResetUnlinkedMedia: () => dispatch(resetUnlinkedMedia()),
    fetchUnlinkedMedia: (merchantId: string, filters?: IFetchUnlinkedMediaParams) =>
        dispatch(fetchUnlinkedMedia(merchantId, filters)),
    fetchMedia: (productId: string) => dispatch(fetchMediaAssets(productId)),
    updateMedia: (
        productId: string,
        revision: string,
        mediaMap: IMediaAssetMap,
        operations: IUpdateMediaAssetOperation[],
    ) => dispatch(mutateMediaAssets(productId, revision, mediaMap, operations)),
    onUpdateLinksState: (merchant_id: string, mediaMap: IMediaAssetMap) =>
        dispatch(markMediaAsLinked(merchant_id, mediaMap)),
    onLinkMedia: (
        productId: string,
        revision: string,
        mediaMap: IMediaAssetMap,
        operations: IUpdateMediaAssetOperation[],
    ) => dispatch(linkMediaToProduct(productId, revision, mediaMap, operations)),
    onSnackbarAdd: (params: SnackbarAddPayload) => dispatch(snackbarAdd(params)),
});

export default connect<IReduxProps, IDispatchProps, IProps>(mapStateToProps, mapDispatchToProps)(ManageMediaDialog);
